import React from "react";
import PropTypes from "prop-types";
import Yup from "yup";
import { Form, Formik } from "formik";
import { get, has } from "lodash";
import { withStyles } from "@mui/styles";
import {
  MenuItem,
  TextField,
  Select,
  Button,
  FormControl,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SimpleSnackbar from "common/components/SimpleSnackbar";
import Resource from "common/components/Resource";
import { hasError, getError } from "common/helpers/formik";

import EnhancedInput from "common/components/EnhancedInput";
import ReactTextEditor from "common/components/ReactTextEditor";
import { Translation } from "react-i18next";
import ImageUploader from "core/ImageUploader";
import { ValidImageFileTypes, MAX_FILE_SIZE } from "common/constants";

const validationSchema = (t) =>
  Yup.object().shape({
    title: Yup.string().required(t("services_create.title_error")),
    category: Yup.string().required(t("services_create.category_error")),
    body: Yup.string().required(
      t("general_form_errors.required", { label: "Body" }),
    ),
    location: Yup.string(),
    hours: Yup.string(),
    image: Yup.mixed()
      .nullable()
      .notRequired()
      .test("file", t("services_create.image_type_error"), (value) => {
        if (!value || !value.name) return true; // Skip validation if no file is uploaded
        return ValidImageFileTypes.includes(
          value.name.split(".").pop().toLowerCase(),
        );
      })
      .test("file", t("services_create.image_size_error"), (value) => {
        if (!value || !value.size) return true; // Skip validation if no file is uploaded
        return value.size <= MAX_FILE_SIZE; // Validate file size
      }),
  });

const ServiceForm = ({ classes, handleSubmit, initialValues, isEdit }) => (
  <Translation>
    {(t) => (
      <Formik
        enableReinitialize
        validationSchema={validationSchema(t)}
        onSubmit={handleSubmit}
        initialValues={initialValues}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          handleChange,
          setFieldTouched,
          setFieldValue,
          status,
        }) => {
          const checkError = hasError(errors, touched);
          const errorMsg = getError(errors, touched);

          return (
            <Form>
              <Grid container direction="column" spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <FormControl
                    className={classes.formControl}
                    error={checkError("category")}
                  >
                    <Resource
                      name="service_categories"
                      render={(categories) => (
                        <Select
                          name="category"
                          id="category"
                          value={values.category}
                          onChange={handleChange}
                        >
                          {categories.map((item) => (
                            <MenuItem value={item.key} key={item.key}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {checkError("category") ? (
                      <FormHelperText>{errorMsg("category")}</FormHelperText>
                    ) : (
                      <FormHelperText>
                        {t("services_create.category_hint")}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <FormControl className={classes.formControl}>
                    <TextField
                      error={checkError("title")}
                      id="title"
                      type="text"
                      label={t("services_create.title_label")}
                      margin="normal"
                      helperText={errorMsg("title")}
                      onChange={handleChange}
                      value={values.title}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <EnhancedInput
                    id="body"
                    type="text"
                    name="body"
                    label={
                      <FormHelperText style={{ fontSize: "0.9rem" }}>
                        {t("services_create.body_label")}
                      </FormHelperText>
                    }
                    disabled
                    rows="6"
                    error={touched.body && !!errors.body}
                    onChange={handleChange}
                    value={values.body}
                    style={{ display: "none" }}
                  />
                  <ReactTextEditor
                    bounds=".html_editor"
                    defaultValue={initialValues.body}
                    value={values.body}
                    handleChange={(html) => {
                      setFieldValue("body", html);
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <FormControl className={classes.formControl}>
                    <TextField
                      error={checkError("location")}
                      id="location"
                      type="text"
                      label={t("services_create.location_label")}
                      margin="normal"
                      helperText={errorMsg("location")}
                      onChange={handleChange}
                      value={values.location}
                    />
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <FormControl className={classes.formControl}>
                    <TextField
                      error={checkError("hours")}
                      id="hours"
                      type="text"
                      label={t("services_create.hours_label")}
                      margin="normal"
                      helperText={errorMsg("hours")}
                      onChange={handleChange}
                      value={values.hours}
                    />
                  </FormControl>
                </Grid>
                {!initialValues.ishilo && (
                  <Grid
                    size={{ xs: 5 }}
                    style={{
                      paddingLeft: "10px",
                      paddingTop: "10px",
                    }}
                  >
                    <ImageUploader
                      buttonId="service_image_uploader_btn" 
                      inputId="service_image_input"
                      imageStyle={{
                        width: "100%",
                        height: "100%",
                      }}
                      label={t("services_create.image_title")}
                      captionStyle={{ whiteSpace: "pre-wrap" }}
                      caption={`${t("services_create.image_hint")} \n${t(
                        "services_create.image_note",
                      )}`}
                      initialImageUrl={values.image}
                      touched={touched.image}
                      onChange={(file) => {
                        setFieldValue("image", file, false);
                        setFieldTouched("image", true);
                      }}
                    />
                    {touched.image && errors.image && (
                      <FormHelperText error>{errors.image}</FormHelperText>
                    )}
                  </Grid>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className={classes.button}
                  disabled={isSubmitting}
                >
                  {isEdit
                    ? t("services_create.update_btn")
                    : t("services_create.create_btn")}
                </Button>
                {has(errors, "http") && (
                  <SimpleSnackbar
                    message={t("general_form_errors.error_response")}
                  />
                )}
              </Grid>
              {get(status, "success") && (
                <SimpleSnackbar
                  message={
                    isEdit
                      ? t("services_create.update_success")
                      : t("services_create.create_success")
                  }
                />
              )}
            </Form>
          );
        }}
      </Formik>
    )}
  </Translation>
);

const styles = (theme) => ({
  button: {
    marginTop: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    width: "100%",
  },
});

ServiceForm.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default withStyles(styles)(ServiceForm);
